import React from "react";
import EmptyState from "components/EmptyState";
import PendingMovie from "assets/img/svg/Checkmark.svg";
import RejectedIcon from "assets/img/svg/Pendingmovies.svg";
import { Trailers } from "../components/Trailers";
import { MovieFiles } from "../components/MovieFiles";
import Seasons from "../components/Seasons";
import { MovieT } from "../type";

/** --------------------
 *  Reusable Reasons List
 * -------------------- */
const RejectionReasonsBox: React.FC<{ reasons: string[] }> = ({ reasons }) => {
  return (
    <div className="border border-gray-300 rounded-lg px-4 max-w-md bg-white mx-auto">
      <h3 className="font-extrabold text-xl py-2 text-black border-b border-dashed border-gray-300">
        Reasons for rejection
      </h3>
      <ul className="list-none p-0 m-0">
        {reasons.map((reason, index) => (
          <li
            key={index}
            className={`py-2 ${index !== reasons.length - 1 ? "border-b border-dashed border-gray-300" : ""}`}
          >
            {reason}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** --------------------
 *  Pending State Component
 * -------------------- */
const PendingState = () => {
  return (
    <EmptyState
      icon={<img src={PendingMovie} alt="" />}
      title="Movie submitted!"
      titleProps={{
        className: "text-black font-bold",
      }}
      description="Nice one! We’re checking it out now. You’ll hear from us once it’s all good and ready to go live."
    />
  );
};

/** --------------------
 *  Rejected State Component
 * -------------------- */
const RejectedState = () => {
  const rejectionReasons = [
    "Video quality needs improvement",
    "There’s a typo in the movie title",
    "The audio is out of sync",
    "The subtitles are not appearing",
    "The video is buffering too frequently",
    "Certain scenes are too dark to see",
    "The aspect ratio is incorrect",
  ];

  return (
    <div className="max-w-md space-y-3 mx-auto">
      <EmptyState
        icon={<img src={RejectedIcon} alt="" />}
        title="Submission rejected!"
        titleProps={{
          className: "text-black font-bold",
        }}
        description="Your movie wasn't approved. See the reasons below, fix them, and resubmit when you're ready."
      />

      <RejectionReasonsBox reasons={rejectionReasons} />

      <button className="bg-primary hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-3 w-full">
        Fix and resubmit
      </button>
    </div>
  );
};

const Live = ({ movie }: { movie: MovieT }) => {
  return (
    <>
      <Trailers movie={movie} />

      <MovieFiles movie={movie} />

      <Seasons movie={movie} />
    </>
  )
}

/** --------------------
 *  Media Page Component
 * -------------------- */
const Media = ({
  status, movie
}: {
  status: 'pending' | "live" | 'rejected',
  movie: MovieT
}) => {
  return (
    <div className="space-y-6">
      {status === 'pending' && <PendingState />}

      {status === 'rejected' && <RejectedState />}
      {status === 'live' && <Live movie={movie} />}
    </div>
  );
};

export default Media;
